import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoaderBar } from '../components/LoaderBar';
import { useAuth } from '../contexts/auth';
import { CommonUtility } from '../utility';
import { UserService } from '../utility/services/user';
import {
  clearHandoffFailed,
  handoffFailureMessage,
  markHandoffFailed,
  parseHandoffFragment,
} from './sso.helpers.js';

/**
 * Landing for the portal's "Go to WMS": `/sso#code=...`. Posts the one-time code to wms-api and,
 * on success, stores the session exactly as the login page does and goes to the app. On any
 * failure the user sees the login page with a message, never an error page. The fragment is
 * cleared from the address bar before anything else happens so the code is not kept in history.
 */
export function SsoScreen() {
  const navigate = useNavigate();
  const { setUserData } = useAuth();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const { code, next } = parseHandoffFragment(window.location.hash);
    window.history.replaceState(null, '', window.location.pathname);

    const toLogin = (message: string) => {
      markHandoffFailed(window.sessionStorage);
      navigate('/login', { replace: true, state: { message } });
    };

    if (!code) {
      toLogin(handoffFailureMessage({ code: 'handoff_code_invalid' }));
      return;
    }

    const run = async () => {
      try {
        // A session left over from another account must not survive the handoff.
        UserService.logout();
        const response = await UserService.handoff({ code });
        UserService.storeUser(response);
        setUserData(response);
        clearHandoffFailed(window.sessionStorage);
        if (next) {
          navigate(next, { replace: true });
        } else if (CommonUtility.isMobileDevice()) {
          navigate('/app/fulfill/waves/list', { replace: true });
        } else {
          navigate('/app/fulfill/orders', { replace: true });
        }
      } catch (error) {
        toLogin(handoffFailureMessage(error));
      }
    };
    void run();
  }, [navigate, setUserData]);

  return <LoaderBar />;
}
